import { get, toArray, omit } from 'lodash-es';
import { getAppraiserUsedTmvPath, selectEmo4346Context } from 'client/data/utils/used-tmv-experiment';

// Models
import {
  VehicleModel,
  buildNoSubmodelVehiclePath,
  buildMakeModelYearsPath,
  buildEstimatedStyleIdsPath,
  buildMakeModelDefaultYearV2,
} from 'client/data/models/vehicle-v2';
import { PartnerOfferModel, buildPartnerOfferVehicleInfoPath } from 'client/data/models/partner-offer-model';
import {
  AppraisalModel,
  buildAllStylesAppraisalPath,
  buildAllStylesAppraisalFilteredPath,
  VEHICLE_MILEAGE_PER_YEAR,
} from 'client/data/models/appraisal';
import { getPageLinksPath, LinkWidgetsModel } from 'client/data/models/link-widgets';
import { buildTcoPath, CostToOwnModel, hasTcoData } from 'client/data/models/cost-to-own';
import { buildStylesBasicPathFromVin, VehicleVinModel } from 'client/data/models/vehicle-vin';
import { CmsModel } from 'client/data/models/cms';
import { MultiOfferModel, searchByVinPath } from 'client/data/models/multi-offer';
import { getAppraiserStylesPath } from 'client/data/utils/appraiser-preload-experiment';

// Actions
import { updateVehicleByStyleId } from 'client/actions/vehicle';
import { redirect } from 'client/actions/redirect';

// Utils
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { makeNiceName as getNiceName } from 'site-modules/shared/utils/nice-name';
import { handle404Error } from 'site-modules/shared/utils/errors';
import { getParamsWithMakes } from 'client/utils/router-params';
import { filterPubStates } from 'site-modules/shared/utils/publication-states';
import { getQuery } from 'client/utils/location';
import { setCriticalCss } from 'client/actions/critical-css';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { getAppraisalLink } from 'site-modules/shared/utils/appraisal-link-constructor';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { setVenomTestHeader } from 'client/actions/venom-test-header';

// Constants
import { HTTP_NOT_FOUND, HTTP_BAD_REQUEST } from 'client/utils/http-status';
import { ALLOWED_PUB_STATES } from 'site-modules/shared/constants/appraisal/appraisal';
import { APPRAISER_HIGHLIGHTS_CONTENT_FILE_PATH } from 'site-modules/shared/constants/appraiser-highlights';
import { REVIEWS_MODULE_USER_REVIEW_CONTENT_PATH } from 'site-modules/shared/components/offers-reviews-module/constants';
import {
  APPRAISER_STYLE_HIGHLIGHTS_PAGE_NAME,
  APPRAISER_STYLE_PAGE_NAME,
} from 'site-modules/appraiser/constants/appraiser';
import { EMO_4379_CAMPAIGN, EMO_4379_RECIPES, isEmo4379Eligible } from 'client/site-modules/appraiser/utils/emo-4379';

import { getAppraiserSeoObject, getSelectedStyleId, transformStylesToStyleIds } from './appraiser-utils';
import { getAppraiserHighlightsSeoObject } from './appraiser-highlights-utils';

const preload = (store, props, isPreloadAboveFold = false, isHighlightsPage = false) => {
  const params = getParamsWithMakes(props);
  const { make, model, year } = params;
  const { vin, styleIds, modsId } = getQuery(props.location);
  const makeNiceName = getNiceName(make);
  const modelNiceName = getNiceName(model);

  const { pageContext } = store.getState();
  const isAppExtensionPage = pageContext?.isAppExtensionPage;
  const preloader = new ModelPreloader(store);

  if (isHighlightsPage) {
    preloader.resolve(`content["${APPRAISER_HIGHLIGHTS_CONTENT_FILE_PATH}"]`, CmsModel);
  }

  if (vin && !validation.validateVIN(vin)) {
    // If VIN is invalid, redirect to the /appraisal-value/ pages without VIN and styles in the URL
    store.dispatch(
      redirect({
        path: getAppraisalLink({
          make,
          model,
          year,
          queryParams: omit(getQuery(props.location), ['vin', 'styleIds']),
          isAppExtensionPage,
        }),
      })
    );

    // Short circuit here, because we are going to redirect (so no point in continuing)
    return preloader.load();
  }

  // resolve model for YearDropdown
  const yearsPath = buildMakeModelYearsPath({ make: makeNiceName, model: modelNiceName });
  const vehiclePath = buildNoSubmodelVehiclePath({ make: makeNiceName, model: modelNiceName, year });
  const estimatedStyleIdsPath = buildEstimatedStyleIdsPath({ make: makeNiceName, model: modelNiceName, year });
  const vinPath = buildStylesBasicPathFromVin({ vin });
  const vehicleInfoPath = buildPartnerOfferVehicleInfoPath({ vin, modsId });
  const isEmo4379Chal1Assigned =
    vin &&
    ExperimentUtil.getForcedOrAssignedRecipeName({
      state: store.getState(),
      campaignName: EMO_4379_CAMPAIGN,
      defaultVal: EMO_4379_RECIPES.CTRL,
    }) === EMO_4379_RECIPES.CHAL1;

  const emo4182Recipe = ExperimentUtil.getForcedOrAssignedRecipeName({
    state: store.getState(),
    campaignName: 'EMO-4182',
    defaultVal: 'ctrl',
  });

  const stylesPath = getAppraiserStylesPath({
    pageName: pageContext.page.name,
    location: props.location,
    emo4182Recipe,
    make: makeNiceName,
    model: modelNiceName,
    year,
  });

  // this page uses make list
  preloader.resolve('makesList', VehicleModel);

  preloader.set('estimatedAppraisalVin', VehicleModel, vin);

  if (vinPath) preloader.resolve(vinPath, VehicleVinModel);

  if (!isPreloadAboveFold) {
    preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
    preloader.resolve(`content["${REVIEWS_MODULE_USER_REVIEW_CONTENT_PATH}"]`, CmsModel);
  }

  let selectedStyleId;
  let modelYearId;

  /**
   * IMPORTANT: resolve path to get vehicle data that subsequent preloaders DEPENDS ON should be within this block of preloaders.
   * DO NOT ADD paths that subsequent preloaders DO NOT depend on it, those should go in the subsequent preloader.resolve blocks
   */
  preloader.resolve(yearsPath, VehicleModel);
  preloader.resolve(vehiclePath, VehicleModel);
  preloader.resolve(estimatedStyleIdsPath, VehicleModel);
  preloader.resolve(stylesPath, VehicleModel);
  if (isEmo4379Chal1Assigned) preloader.resolve(searchByVinPath.build(vin), MultiOfferModel);

  const estimatedMileage = VEHICLE_MILEAGE_PER_YEAR * (new Date().getFullYear() - year);
  preloader.set('estimatedAppraisalMileage', VehicleModel, estimatedMileage);

  return preloader
    .load()
    .catch(err => {
      // catches 400 and 404 calls from the vehicle data preloader.resolve and throws handle404Errors explicitly so it can render the 404 page
      if ([HTTP_NOT_FOUND, HTTP_BAD_REQUEST].includes(err.status)) {
        throw handle404Error({ errorMessage: `[${APPRAISER_STYLE_PAGE_NAME}] ${err.message}` });
      }

      // throw all other errors as is, so it can be handled by VENOM
      throw err;
    })
    .then(modelState => {
      const vehicleModel = modelState.get(vehiclePath, VehicleModel);
      const estimatedStyleIds = modelState.get(estimatedStyleIdsPath, VehicleModel);
      const allStylesForVehicle = modelState.get(stylesPath, VehicleModel);
      const allStyleIdsForVehicle = Array.isArray(allStylesForVehicle)
        ? transformStylesToStyleIds(allStylesForVehicle)
        : [];

      const years = modelState.get(yearsPath, VehicleModel);
      modelYearId = get(vehicleModel, 'modelYearId');
      if (!filterPubStates(toArray(years), ALLOWED_PUB_STATES).some(filteredYear => year === filteredYear.year)) {
        throw handle404Error({ errorMessage: `The year ${year} is not available to select on VAC` });
      }

      if (!modelYearId) {
        throw handle404Error({ errorMessage: `No "modelYearId" was found for ${make} ${model} ${year} appraisal` });
      }

      // Get default style id:
      selectedStyleId = getSelectedStyleId(estimatedStyleIds, styleIds, allStyleIdsForVehicle);

      if (!selectedStyleId) {
        throw handle404Error({
          errorMessage: `No "selectedStyleId" was found for ${make} ${model} ${year} appraisal`,
        });
      }

      // resolve path to get estimated vehicle image
      preloader.resolve(`styles.${selectedStyleId}.photo`, VehicleModel);

      // save the last selected style id so we can pick it up from the model later
      preloader.set('estimatedAppraisalStyle', VehicleModel, selectedStyleId);

      if (!isPreloadAboveFold) {
        preloader.resolve(buildAllStylesAppraisalFilteredPath(params), AppraisalModel);
      }

      preloader.resolve(buildAllStylesAppraisalPath(params), AppraisalModel);

      if (hasTcoData(year) && !isPreloadAboveFold) {
        preloader.resolve(buildTcoPath({ make, model, year }), CostToOwnModel);
      }

      preloader.resolve(buildMakeModelDefaultYearV2(params), VehicleModel);

      if (vin) {
        preloader.resolve(vehicleInfoPath, PartnerOfferModel);
        if (!isEmo4379Chal1Assigned) preloader.resolve(searchByVinPath.build(vin), MultiOfferModel);
      }

      // This was previously in a separate preloader.load() block but can now be consolidated since we are using vehicle-v2 model only
      // resolve path to get used tmv values by style id
      if (!isPreloadAboveFold) {
        preloader.resolve(
          getAppraiserUsedTmvPath({
            styleId: selectedStyleId,
            isExtendedReport: true,
            useGraphqlUsedTmv: !isHighlightsPage && selectEmo4346Context(store.getState()).useGraphqlUsedTmv,
          }),
          VehicleModel
        );
      }

      // required for style_id to be populated in edw pixels
      updateVehicleByStyleId(selectedStyleId, modelYearId)(store.dispatch, store.getState);

      const appraisalTabsRecipe = ExperimentUtil.getForcedOrAssignedRecipeName({
        state: store.getState(),
        campaignName: 'EMO-3500',
        defaultVal: 'ctrl',
      });

      const emo4318Recipe = store.getState().mobile
        ? ExperimentUtil.getForcedOrAssignedRecipeName({
            state: store.getState(),
            campaignName: 'EMO-4318',
            defaultVal: 'ctrl',
          })
        : 'ctrl';

      const vinModsRecord = isEmo4379Chal1Assigned ? modelState.get(searchByVinPath.build(vin), MultiOfferModel) : null;
      const emo4379Eligible = isEmo4379Eligible(vinModsRecord);
      const emo4379Recipe = emo4379Eligible ? EMO_4379_RECIPES.CHAL1 : EMO_4379_RECIPES.CTRL;

      // For tracking page metrics in New Relic
      store.dispatch(
        setVenomTestHeader({
          'emo-4182': emo4182Recipe,
          'emo-4318': emo4318Recipe,
          [EMO_4379_CAMPAIGN.toLowerCase()]: emo4379Recipe,
        })
      );

      store.dispatch(
        setCriticalCss({
          hasQueryVin: !!vin, // 2 values
          isAppExtensionPage, // 2 values
          appraisalTabsRecipe, // 2 values
          emo4318Recipe, // 2 values
          displayType: emo4379Recipe === EMO_4379_RECIPES.CHAL1 ? 'appraisals-renewal' : 'appraisals', // 2 values
        })
      );

      return preloader.load();
    });
};

export const getPageDefinitionConfiguration = ({ isHighlightsPage } = {}) => ({
  name: isHighlightsPage ? APPRAISER_STYLE_HIGHLIGHTS_PAGE_NAME : APPRAISER_STYLE_PAGE_NAME,
  category: 'used_model_mydp',
  preloadAboveFold: (store, props) => preload(store, props, true, isHighlightsPage),
  preload: (store, props) => preload(store, props, false, isHighlightsPage),
  seo: (state, props) =>
    isHighlightsPage ? getAppraiserHighlightsSeoObject(state, props) : getAppraiserSeoObject(state, props),
  options: {
    scrollBreakpoint: true,
    disableAdRefreshOnScroll: true,
  },
});
